// © Microsoft Corporation. All rights reserved.
import { AppType, PageType, allEnvironments } from "./Constants";
import { AudienceModeType, BrowserType } from "@msnews/experiences-constants";
import { ConfigOrigin } from "@msnews/core-contracts";
import { UrlUtility } from "./UrlUtility";
import { UrlSearchParamsHelper } from "./UrlSearchParamsHelper";
import { shouldUseGitConfigs } from "@msnews/experimentation-helpers";
/** The Environment class information about Web Worker environment. */
class Environment {
    /** Returns activity id. */
    get activityId() {
        return this._activityId;
    }
    /** Returns app type. */
    get apptype() {
        return this._apptype;
    }
    /** Returns audienceMode. */
    get audienceMode() {
        return this._audienceMode;
    }
    /** Returns _browser. */
    get browser() {
        return this._browser;
    }
    /** Returns current flights. */
    get currentFlights() {
        return this._currentFlights;
    }
    /** Returns configIndexDoc. */
    get configIndexDocId() {
        return this._configIndexDocId;
    }
    /** Returns config root url. */
    get configRootUrl() {
        const url = new URL("", this._configRootUrl);
        return url;
    }
    /** Returns domain URL. */
    get domain() {
        return this._domain;
    }
    /** Returns a flag indicating whether config preload by web worker is disabled or not. */
    get disableWWConfigPreload() {
        return this._disableWWConfigPreload;
    }
    /** Returns a flag indicating whether config service is enabled or not. */
    get enableConfigService() {
        return this._enableConfigService;
    }
    /** Returns a flag indicating whether configs will be fetched from CMS:PPE or not. */
    get enableCMSPPEConfigs() {
        return this._enableCMSPPEConfigs;
    }
    /** Returns a flag indicating whether multiple config retrieval by web worker is enabled or not. */
    get multiConfigRetrievalEnabled() {
        return this._isMultiConfigRetrievalEnabled;
    }
    /** Returns a flag indicating whether content preview caching by web worker is disabled or not. */
    get disableContentPreviewCache() {
        return this._disableContentPreviewCache;
    }
    /** Returns a flag indicating whether one service 'myfeed' fetch call by web worker is disabled or not. */
    get isMyFeedFetchDisabled() {
        return this._isMyFeedFetchDisabled;
    }
    /**
     * Returns a flag indicating whether the one service feed fetch call should send activityID
     * in the request header or as a QSP in the URL (current behavior is via QSP).
     */
    get sendFeedCallActivityIdInHeader() {
        return this._sendFeedCallActivityIdInHeader;
    }
    /** Returns Config Service Base Url. */
    get configServiceBaseUrl() {
        return this._configServiceBaseUrl;
    }
    /** Returns cache busting config url. */
    get cacheBustingConfigUrl() {
        return this._cacheBustingConfigUrl;
    }
    /** Returns deviceFormFactor. */
    get deviceFormFactor() {
        return this._deviceFormFactor;
    }
    /** Returns env. */
    get env() {
        return this._env;
    }
    /** Returns feature flags. */
    get featureFlags() {
        return this._featureFlags;
    }
    /** The schema://domain to be used for the main feed URL */
    get feedServiceUrlBase() {
        return new URL("", this._feedServiceUrlBase);
    }
    /** The schema://domain to be used for the main feed URL for enterprise */
    get enterpriseServiceUrlBase() {
        if (!this._enterpriseServiceUrlBase) {
            return undefined;
        }
        return new URL("", this._enterpriseServiceUrlBase);
    }
    /** Returns the content service base url */
    get ContentServiceUrlBase() {
        // If I mutate the same object, I will need to revert to the original state somewhere.
        // For safety reason, it is better to use a new Object
        return new URL("", this._contentServiceUrlBase);
    }
    /** Returns debug status. */
    get isDebug() {
        return this._isDebug;
    }
    /** Returns locale. */
    get locale() {
        return this._locale;
    }
    /** Returns latitude. */
    get latitude() {
        return this._latitude;
    }
    /** Returns weather home location cookie */
    get homeLocationCookie() {
        return this._weatherHomeLocationCookie;
    }
    /** Returns weather detect location cookie */
    get detectLocationCookie() {
        return this._weatherDetectLocationCookie;
    }
    /** Returns longitude. */
    get longitude() {
        return this._longitude;
    }
    /** Returns page type. */
    get pagetype() {
        return this._pagetype;
    }
    /** Returns signedIn state. */
    get signedIn() {
        return this._signedIn;
    }
    /** Returns user id. */
    get userId() {
        return this._userId;
    }
    /** Returns ocid string. */
    get ocid() {
        return this._ocid;
    }
    /** Returns Operating system type. */
    get os() {
        return this._os;
    }
    /** Returns pagetype given by pcs. */
    get pcsPagetype() {
        return this._pcsPagetype;
    }
    /** Returns apptype given by pcs. */
    get pcsApptype() {
        return this._pcsApptype;
    }
    /** Returns locale object given by pcs. */
    get pcsLocale() {
        return this._pcsLocale;
    }
    /** Returns domain object given by pcs. */
    get pcsDomain() {
        return this._pcsDomain;
    }
    /** Returns cache busting identifier given by pcs. */
    get cacheBustingIdentifier() {
        return this._cacheBustingIdentifier;
    }
    /** Returns cache bypass value from stored cookie. */
    get cacheBypassValue() {
        return this._cacheBypassValue;
    }
    /**
     * The performance time offset from the main thread to add
     * to our web-worker timings to normalize with the main thread timeline
     */
    get perfTimeOffset() {
        return this._perfTimeOffset;
    }
    /** Returns _isEnterpriseCompliantNTP */
    get isEnterpriseCompliantNTP() {
        return this._isEnterpriseCompliantNTP;
    }
    /** Returns Bundle build version */
    get buildVersion() {
        return this._buildVersion;
    }
    /** True if this is git configs flight */
    get isGitConfigs() {
        return this._isGitConfigs;
    }
    /** Fetches local dev environment for client resolver */
    get configOrigins() {
        return this._configOrigins;
    }
    /** Returns _oneServiceApiKey */
    get oneServiceApiKey() {
        return this._oneServiceApiKey;
    }
    /**
     * Get all search params
     * @returns all search params
     */
    getSearchParams() {
        return this.searchParams && this.searchParams.getSearchParams();
    }
    /**
     * Checks if prefetch is enabled for the current AppType
     */
    get isPrefetchEnabled() {
        return !this.isMyFeedFetchDisabled &&
            (this.apptype === AppType.EdgeChromium ||
                this.apptype === AppType.Edge ||
                this.apptype === AppType.MMX ||
                this.apptype === AppType.MicrosoftNews ||
                this.apptype === AppType.Hub ||
                this.apptype === AppType.Windows ||
                this.apptype === AppType.Homepage);
    }
    /**
     * Extract query string param in the url.
     * @param name - Query param name
     * @returns Query param value
     */
    getQueryParameter(name) {
        const queryValue = this.searchParams.get(name);
        if (typeof queryValue === "undefined") {
            return null;
        }
        return decodeURIComponent(queryValue.replace(/\+/g, " ")) || "";
    }
    /**
     * Get locale object from locale string. e.g. en-us, en-ca, fr-ca
     */
    getLocaleObject() {
        const localeSegmentsArray = this.locale.split("-");
        let countOfSegmentsNeededForLanguagePart = localeSegmentsArray.length;
        if (countOfSegmentsNeededForLanguagePart > 2) {
            countOfSegmentsNeededForLanguagePart = countOfSegmentsNeededForLanguagePart - 1;
        }
        const langArray = localeSegmentsArray.slice(0, countOfSegmentsNeededForLanguagePart);
        langArray.join("-");
        return {
            language: langArray[0],
            market: langArray[1]
        };
    }
    /**
     * Initialize the environment.
     * @param overrideLocationHash - The location href override used in testing
     */
    init(overrideLocationHash) {
        let locationHash = overrideLocationHash || location.hash;
        locationHash = locationHash.charAt(0) === "#" ? locationHash.substring(1) : locationHash;
        this.searchParams = new UrlSearchParamsHelper(locationHash);
        this.domainExt = location.hostname.endsWith(".cn") ? "cn" : "com";
        // This only finds multiples of item=key:value qsp. If keys are the same, first given will be used (similar to UrlSearchParamsHelper).
        this.itemParams = UrlUtility.getParamsWithItems(locationHash);
        this._isDebug = ((this.searchParams.get("debug") || "").toLowerCase() in { 1: 1, true: 1 });
        const aaceValue = this.getQueryParameter("aace");
        this._signedIn = !!aaceValue;
        this.setDomainEnvironment();
        this._pcsApptype = this.getQueryParameter("apptype") || undefined;
        this.setAppType();
        this._pcsPagetype = this.getQueryParameter("pagetype") || undefined;
        this.setPageType();
        const locale = this.getQueryParameter("locale") || undefined;
        this._pcsLocale = locale ? this.tryParseJson(locale) : undefined;
        this._locale = this.pcsLocale &&
            (this.pcsLocale.language
                + "-"
                + (this.pcsLocale.script ? this.pcsLocale.script + "-" : "")
                + this.pcsLocale.market).toLowerCase() || "en-us";
        const servicesEndpoints = this.getQueryParameter("servicesEndpoints") || undefined;
        this._servicesEndpoints = servicesEndpoints && this.tryParseJson(servicesEndpoints) || undefined;
        // Get from headdata if possible.
        // Else using location.origin is okay (current MMX scenario) since it'd use .cn version for China.
        this._feedServiceDomain =
            this._servicesEndpoints &&
                this._servicesEndpoints.feedService &&
                this._servicesEndpoints.feedService.domain || location.origin;
        this._crsVersion = this._servicesEndpoints
            && this._servicesEndpoints.crs
            && this._servicesEndpoints.crs.v;
        this.setUserId();
        this._activityId = this.getQueryParameter("aid");
        this._ocid = this.getQueryParameter("ocid") || undefined;
        this._os = this.getQueryParameter("os") || undefined;
        this.setAudienceModeType();
        const dataInfo = this.getQueryParameter("dataInfo");
        this._currentFlights = ((/f:\s*([^;]+)/i.exec(dataInfo) || {})[1] || "").toLowerCase();
        this._configIndexDocId = this.getQueryParameter("configIndexDocId");
        // Make sure that _apptype, _crsVersion, _configIndexDocId and _currentFlights are set before calling this function.
        this._isGitConfigs = this.useGitConfigs();
        this._configOrigins = this.setConfigOrigin();
        this._browser = JSON.parse(this.getQueryParameter("browser")) || undefined;
        const cmsItem = this.itemParams.find(item => item.key === "cms");
        this._enableCMSPPEConfigs = !!(cmsItem && cmsItem.value === "ppe");
        this._pcsDomain = this.getQueryParameter("domain") || undefined;
        this._featureFlags = JSON.parse(this.getQueryParameter("featureFlags")) || {};
        this._deviceFormFactor = this.getQueryParameter("deviceFormFactor") || undefined;
        this._weatherHomeLocationCookie = this.getQueryParameter("weaSpartanHomeLocation") ? this.getQueryParameter("weaSpartanHomeLocation") : this.getQueryParameter("weaPeregrineHomeLocation");
        this._weatherDetectLocationCookie = this.getQueryParameter("weaDetectLocation") ? this.getQueryParameter("weaDetectLocation") : this.getQueryParameter("weaPeregrineDetectLocation");
        this._disableWWConfigPreload = this.featureFlags.disableCachingConfigs
            ? this.featureFlags.disableCachingConfigs === "true"
            : false;
        this._enableConfigService = this.featureFlags.enableConfigService
            ? this.featureFlags.enableConfigService === "true"
            : true;
        this._disableContentPreviewCache = this.featureFlags.disableContentPreviewCache
            ? this.featureFlags.disableContentPreviewCache === "true"
            : false;
        this._isMyFeedFetchDisabled = this.featureFlags.disableMyFeedFetch
            ? this.featureFlags.disableMyFeedFetch === "true"
            : false;
        this._sendFeedCallActivityIdInHeader = this.shouldSendFeedCallActivityIdInHeader();
        this._contentServiceUrlBase = this.getContentServiceUrlBase();
        this._feedServiceUrlBase = this.getFeedServiceUrlBase();
        this._enterpriseServiceUrlBase = this.getEnterpriseServiceUrlBase();
        this._configRootUrl = this.getConfigRootUrl();
        this._latitude = this.getQueryParameter("lat");
        this._longitude = this.getQueryParameter("long");
        this._isMultiConfigRetrievalEnabled = this.useMultiConfigsRetrieval();
        this._configServiceBaseUrl = new URL(this.getConfigServiceBaseUrl(this._isMultiConfigRetrievalEnabled));
        this._cacheBustingIdentifier = this.getQueryParameter("cbid");
        this._cacheBustingConfigUrl = new URL(this.getCacheBustingConfigUrl());
        this._cacheBypassValue = parseInt(this.getQueryParameter("cbypass")) || 0;
        const mainTimeOrigin = parseInt(this.getQueryParameter("mainTimeOrigin"));
        this._perfTimeOffset = !isNaN(mainTimeOrigin)
            ? performance.timeOrigin - mainTimeOrigin
            : 0;
        this._isEnterpriseCompliantNTP = this.checkEnterpriseCompliantNTP();
        this._buildVersion = this.getQueryParameter("buildVersion");
        this._oneServiceApiKey = this.getOneServiceApiKey();
    }
    /**
     * Get the ApiKey that should be used by the current AppType
     * Please work OneService (aka: IDX) before updating it as this can break features
     * even if the call succeeds.
     */
    getOneServiceApiKey() {
        let apiKey;
        switch (this.apptype) {
            case AppType.WindowsShell:
                apiKey = "pWw5OmQehOA0XNfgcgrTrwEJZJJJzE83ovtTQx6JRG";
                break;
            case AppType.NewsAndInterests:
            case AppType.WindowsNewsPlus:
                apiKey = "AEbyVYqTQAPvg4lWwMSFHaLkDzl3weRKKIDDInLQUj";
                break;
            default:
                apiKey = "0QfOX3Vn51YCzitbLaRkTTBadtWpgTN8NZLW0C1SEM";
        }
        return apiKey;
    }
    /**
     * Set the domain and cdn urls.
     * @param domainEnvironment - The domain environment
     */
    setDomainEnvironment() {
        this._env = this.getQueryParameter("env");
        const newEnvironment = allEnvironments[this._env];
        if (newEnvironment) {
            this._domain = newEnvironment.domain;
        }
        else {
            this._env = "prod";
            this._domain = allEnvironments.prod.domain;
        }
    }
    /** Set the user id. */
    setUserId() {
        const muid = this.getQueryParameter("muid");
        if (muid) {
            this._userId = `m-${muid}`;
        }
    }
    /**
     * Set the app type.
     * @param apptype - The app type
     */
    setAppType() {
        let apptype = this.getQueryParameter("apptype") || "";
        apptype = apptype.toLowerCase();
        switch (apptype) {
            case "mmx":
                this._apptype = AppType.MMX;
                break;
            case "edge":
                this._apptype = AppType.Edge;
                break;
            case "edgechromium":
                this._apptype = AppType.EdgeChromium;
                break;
            case "microsoftnews":
                this._apptype = AppType.MicrosoftNews;
                break;
            case "firstparty":
                this._apptype = AppType.FirstParty;
                break;
            case "hub":
                this._apptype = AppType.Hub;
                break;
            case "office":
                this._apptype = AppType.Office;
                break;
            case "views":
                this._apptype = AppType.Views;
                break;
            case "superapp":
                this._apptype = AppType.SuperApp;
                break;
            case "windows":
                this._apptype = AppType.Windows;
                break;
            case "homepage":
                this._apptype = AppType.Homepage;
                break;
            case "windowsshell":
                this._apptype = AppType.WindowsShell;
                break;
            case "newsandinterests":
                this._apptype = AppType.NewsAndInterests;
                break;
            case "windowsnewsplus":
                this._apptype = AppType.WindowsNewsPlus;
                break;
            default:
                this._apptype = AppType.Unknown;
        }
    }
    /**
     * Set the page type.
     * @param pagetype - The page type
     */
    setPageType() {
        let pagetype = this.getQueryParameter("pagetype") || "";
        pagetype = pagetype.toLowerCase();
        switch (pagetype) {
            case "mmx":
                this._pagetype = PageType.MMX;
                break;
            case "dhp":
                this._pagetype = PageType.DHP;
                break;
            case "ntp":
                this._pagetype = PageType.NTP;
                break;
            case "windowshp":
                this._pagetype = PageType.WindowsHP;
                break;
            case "newsbar":
                this._pagetype = PageType.NewsBar;
                break;
            default:
                this._pagetype = PageType.Unknown;
        }
    }
    /**
     * Set the Audience mode type.
     */
    setAudienceModeType() {
        const audienceModeString = this.getQueryParameter("audienceMode") || "";
        // Don't know if the previous logic is correct, keep it for now
        this._audienceMode = environment.signedIn && (this.getQueryParameter("child") || this.getQueryParameter("query") === AudienceModeType.Kids)
            ? AudienceModeType.Kids
            : audienceModeString.toLowerCase() || AudienceModeType.Adult;
    }
    /**
     * Attempts to parse a given string to a JSON object
     * @param {string} jsonString - The string to parse.
     * @returns {any} - the parsed object if successful, or null otherwise.
     */
    tryParseJson(jsonString) {
        try {
            return JSON.parse(jsonString);
        }
        catch (_a) {
            return undefined;
        }
    }
    /** Gets the FeedService base url */
    getFeedServiceUrlBase() {
        if (!this._feedServiceDomain) {
            return;
        }
        const url = new URL("/service/", this._feedServiceDomain);
        if (location.host.includes("localhost.msn.com") || this.getQueryParameter("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }
        const searchParam = this.getQueryParameter("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }
        return url;
    }
    /** Gets the Enterprise Service base url */
    getEnterpriseServiceUrlBase() {
        const enterpriseFeedServiceDomain = this._servicesEndpoints &&
            this._servicesEndpoints.enterpriseFeedService &&
            this._servicesEndpoints.enterpriseFeedService.domain;
        if (!enterpriseFeedServiceDomain) {
            return;
        }
        const url = new URL("", enterpriseFeedServiceDomain);
        if (location.host.includes("localhost.msn.com") || this.getQueryParameter("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }
        const searchParam = this.getQueryParameter("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }
        return url;
    }
    /** Gets the content service base url */
    getContentServiceUrlBase() {
        if (!this._feedServiceDomain) {
            return;
        }
        let url;
        // Temporarily hardcode endpoint for MMX flight, when flight is off, we want to use hardcoded value.
        // After the flight, we hope to start using the regular logic for MMX
        const isAppTypeMMX = this.apptype === AppType.MMX;
        if (isAppTypeMMX && (!this.currentFlights || this.currentFlights.indexOf("prg-mmxcontent-t") === -1)) {
            url = new URL("./content/", `https://assets.msn.${this.domainExt}`);
        }
        else {
            url = new URL("/content/", this._feedServiceDomain);
        }
        if (location.host.includes("localhost.msn.com") || this.getQueryParameter("env") === "dev") {
            url.searchParams.set("env.content", "dev");
        }
        const searchParam = this.getQueryParameter("env.content");
        if (searchParam) {
            url.searchParams.set("env.content", searchParam);
        }
        return url;
    }
    /** Gets the Config root url */
    getConfigRootUrl() {
        const url = new URL("", this.getQueryParameter("configRootUrl") || `https://assets.msn.${this.domainExt}/config/v1/`);
        if (location.host.includes("localhost.msn.com") || this.getQueryParameter("env") === "dev") {
            url.searchParams.set("env.config", "dev");
        }
        const searchParam = this.getQueryParameter("env.config");
        if (searchParam) {
            url.searchParams.set("env.config", searchParam);
        }
        return url;
    }
    /**
     * Get cache busting config url.
     * @returns {string} - cache busting config url.
     */
    getCacheBustingConfigUrl() {
        const relativePath = `/resolver/api/cbid/${this.pcsApptype || "NoAppType"}`;
        // int and localhost to use int.msn.com for config service.
        if (this._env === "int") {
            return "https://int.msn.com" + relativePath;
        }
        return location.origin + relativePath;
    }
    /**
     * Get config service base url
     * @param {isMultiConfigRetrievalEnabled} - if multiple config retrieval is supported.
     * @returns {string} - config service base url.
     */
    getConfigServiceBaseUrl(isMultiConfigRetrievalEnabled = true) {
        const relativePath = this.isGitConfigs
            ? "/resolver/api/resolve/v3/config/"
            : `/resolver/api/resolve/${isMultiConfigRetrievalEnabled ? "v2/" : ""}configindex/`;
        // int and localhost to use int.msn.com for config service.
        if (this._env === "int") {
            return "https://int.msn.com" + relativePath;
        }
        return location.origin + relativePath;
    }
    /**
     * Function checks if ConfigService should return multiple configs
     * @returns true if supported else false.
     */
    useMultiConfigsRetrieval() {
        const param = this.getQueryParameter("isMultiConfigRetrievalEnabled");
        if (param) {
            return param.toLocaleLowerCase() === "true";
        }
        // PCS flag to turn on/off multi config retrieval.
        if (this.featureFlags
            && this.featureFlags.isMultiConfigRetrievalEnabled) {
            return this.featureFlags.isMultiConfigRetrievalEnabled === "true";
        }
        return true;
    }
    /**
     * Function checks if Git configs is enabled
     * @returns true if supported else false.
     */
    useGitConfigs() {
        const param = this.getQueryParameter("isGitConfig");
        if (param) {
            return param.toLocaleLowerCase() === "true";
        }
        const flights = this._currentFlights.split(",");
        return shouldUseGitConfigs(this._crsVersion, flights, this._configIndexDocId, this._pcsApptype);
    }
    /**
     * Function checks if we should send the activityID in the request header instead of sending it as a QSP in the URL.
     * @returns true if supported else false.
     */
    shouldSendFeedCallActivityIdInHeader() {
        // Use feature flag as a switch to disable the feature (will need a PCS deployment).
        if (this.featureFlags && this.featureFlags.disableActivityIdInHeader && this.featureFlags.disableActivityIdInHeader === "true") {
            return false;
        }
        return this.apptype === AppType.Edge || this.apptype === AppType.Hub;
    }
    /**
    * Checks if enterprise compliant NTP
    * This manual check is needed since we don't have configs at this point
    */
    checkEnterpriseCompliantNTP() {
        return this.apptype === AppType.EdgeChromium &&
            this.audienceMode === AudienceModeType.Enterprise &&
            !!this.browser &&
            this.browser.browserType === BrowserType.EdgeChromium &&
            parseInt(this.browser.version) >= 87;
    }
    /**
     * Indicates whether configs should be fetched from local repo based on conditions and overrides
     */
    setConfigOrigin() {
        // Default to production
        const configOrigins = { feature: ConfigOrigin.Production, loc: ConfigOrigin.Production };
        if (__WEBPACK_LOCAL_GITCONFIGS_LOC__) {
            configOrigins.feature = ConfigOrigin.Local;
            configOrigins.loc = ConfigOrigin.Local;
        }
        else if (__WEBPACK_LOCAL_GITCONFIGS__) {
            configOrigins.feature = ConfigOrigin.Local;
            configOrigins.loc = ConfigOrigin.Production;
        }
        // Override based on conditions
        if (!this._isGitConfigs || this.getEnableConfigService()) {
            configOrigins.feature = ConfigOrigin.Production;
            configOrigins.loc = ConfigOrigin.Production;
        }
        return configOrigins;
    }
    /**
     * This is the switch to turn on/off config service operation
     */
    getEnableConfigService() {
        const queryParamOverride = this.getQueryParameter("enableConfigService") || undefined;
        if (queryParamOverride) {
            return queryParamOverride.toLocaleLowerCase() === "true";
        }
        return this._enableConfigService;
    }
}
/** Singleton class instance. */
export const environment = new Environment();
//# sourceMappingURL=Environment.js.map